import { Link, useParams } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import usePageMeta from '@/hooks/usePageMeta';
import { comics, getComic } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams();
  const comic = getComic(id || '');
  usePageMeta({
    title: comic ? `${comic.title}漫画免费全集` : '漫画详情',
    description: comic
      ? `${comic.title}漫画免费全集目录，收录${comic.category}分类全本资源，共${comic.chapters.length}话。${comic.intro}`
      : '漫画免费全集目录聚合阅读',
    keywords: `漫画免费,${comic?.title ?? '漫画'},${comic?.category ?? ''},漫画免费全集,漫画免费全本`,
  });
  const related = comics.filter((c) => c.id !== (id || '') && c.category === comic?.category).slice(0, 5);

  if (!comic) {
    return (
      <main className="mx-auto w-full max-w-7xl px-4 py-20 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-2xl text-foreground-900">未找到该漫画</h1>
        <Link to="/catalog" className="mt-4 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回漫画大全
        </Link>
      </main>
    );
  }

  return (
    <main>
      <section className="bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <nav className="mb-6 flex flex-wrap items-center gap-1 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/catalog" className="hover:text-primary-600">漫画大全</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/catalog?c=${encodeURIComponent(comic.category)}`} className="hover:text-primary-600">{comic.category}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{comic.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row">
            <div className="w-full shrink-0 sm:w-56">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画免费全集封面`}
                  title={`${comic.title} 漫画免费全本资源`}
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-3xl text-foreground-950">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${comic.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-secondary-100 text-secondary-800'}`}>
                  {comic.status}
                </span>
                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">{comic.category}</span>
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">{t}</span>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-foreground-500">人气</dt>
                  <dd className="font-medium text-foreground-900">{comic.views}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">评分</dt>
                  <dd className="font-medium text-accent-600"><i className="ri-star-fill" /> {comic.rating.toFixed(1)}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">作者</dt>
                  <dd className="font-medium text-foreground-900">{comic.author}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">章节</dt>
                  <dd className="font-medium text-foreground-900">{comic.chapters.length} 话</dd>
                </div>
              </dl>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="mb-2 font-heading text-base text-foreground-900">内容简介</h2>
                <p className="text-sm leading-relaxed text-foreground-700">{comic.intro}</p>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-play-fill" /> 立即开始阅读
                </button>
                <button className="flex items-center gap-2 whitespace-nowrap rounded-full bg-secondary-100 px-6 py-2.5 text-sm font-medium text-secondary-800 transition-colors hover:bg-secondary-200">
                  <i className="ri-bookmark-line" /> 收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <h2 className="mb-5 font-heading text-2xl text-foreground-950">漫画免费全集目录</h2>
          <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {comic.chapters.map((chap) => (
              <button
                key={chap.id}
                className="flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="truncate text-sm text-foreground-800">{chap.title}</span>
                <span className="shrink-0 text-xs text-foreground-500">{chap.date} · {chap.views}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="mb-5 flex items-end justify-between">
              <h2 className="font-heading text-2xl text-foreground-950">同类推荐</h2>
              <Link to={`/catalog?c=${encodeURIComponent(comic.category)}`} className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
                更多同类 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}